/**
 * Captures a founder agent invitation token out of the URL fragment, before
 * anything else in the app can observe it.
 *
 * WHY A FRAGMENT AND NOT A QUERY PARAMETER
 * The invitation token is a seven-day bearer credential: presenting it mints a
 * session for an account that has no password yet. A `?invite=` query parameter
 * is sent to the server on the initial page request, so it would be recorded in
 * Vercel's runtime log details (search params are part of what Vercel stores)
 * and would travel in the `Referer` header to any third-party asset the page
 * loads. A fragment is processed by the browser and never leaves it — it is not
 * sent to Vercel, to the application server, or in a referrer.
 *
 * WHY THIS MODULE SELF-EXECUTES ON IMPORT
 * Ordering is the whole point. ES module side effects run in import order and
 * before any statement in the importing module, so importing this file FIRST in
 * src/main.tsx guarantees the fragment is captured and scrubbed before
 * `Sentry.init()` runs, before `<Analytics />` can mount, and before any other
 * module gets a chance to read `window.location`. An exported function that
 * main.tsx called explicitly would still run after every import's side effects.
 * src/lib/agentInviteBootstrap.test.ts pins that import order.
 *
 * WHERE THE TOKEN LIVES
 * In `capturedToken` below — module-scoped browser memory, and nowhere else. It
 * is deliberately never written to a query parameter, localStorage,
 * sessionStorage, a cookie, `history.state`, the console, Sentry context or
 * breadcrumbs, or analytics. The consequence is that a refresh loses it, which
 * is correct: persisting a bearer credential to survive F5 is exactly the thing
 * worth not doing. /welcome asks the invitee to reopen their email instead.
 */

/**
 * Mirrors isWellFormedAgentInviteToken in
 * supabase/functions/_shared/agentInvite.ts, which cannot be imported here —
 * edge functions run on Deno and do not share the src/ module graph (see
 * CLAUDE.md). 32 random bytes as unpadded base64url is always 43 characters.
 * Keep the two in sync by hand.
 */
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;

/** The only route this may act on. */
const WELCOME_PATH = "/welcome";

const FRAGMENT_PREFIX = "#invite=";

/** Module-scoped browser memory. Never persisted, never serialised. */
let capturedToken: string | null = null;

/** The minimum surface this needs, so it can be exercised without a DOM. */
export interface BootstrapWindow {
  location: { pathname: string; hash: string; search: string };
  history: { replaceState(state: unknown, unused: string, url: string): void };
}

export type CaptureOutcome =
  /** A well-formed token was taken into memory and the URL was cleaned. */
  | "captured"
  /** An invite-shaped value was present but unusable; the URL was still cleaned. */
  | "scrubbed"
  /** Nothing to do — not /welcome, or no invite in the URL at all. */
  | "noop";

/**
 * Pure fragment parser. Returns the token only for an exactly-shaped fragment on
 * exactly the welcome route.
 */
export function parseAgentInviteFragment(pathname: string, hash: string): string | null {
  if (pathname !== WELCOME_PATH) return null;
  if (!hash.startsWith(FRAGMENT_PREFIX)) return null;

  const value = hash.slice(FRAGMENT_PREFIX.length);
  return TOKEN_PATTERN.test(value) ? value : null;
}

/** True when the URL carries anything invite-shaped that should not survive. */
function hasInviteResidue(win: BootstrapWindow): boolean {
  if (win.location.hash.startsWith(FRAGMENT_PREFIX)) return true;
  // A query-string token is never accepted (the emailed link has never used one
  // — this flow has not been deployed or emailed from a hosted project). It is
  // still scrubbed rather than merely ignored, so a stray or hand-crafted
  // `?invite=` cannot linger in the address bar, the referrer, or a shared URL.
  return new URLSearchParams(win.location.search).has("invite");
}

/**
 * Reads the fragment, keeps a valid token in memory, and rewrites the URL to a
 * bare `/welcome` — dropping the fragment AND any query string. Passes `null`
 * as the history state so the token cannot end up in `history.state` either.
 *
 * Safe during SSR and prerendering: with no `window`, it does nothing.
 */
export function captureAgentInviteFromFragment(
  win: BootstrapWindow | undefined = typeof window === "undefined"
    ? undefined
    : (window as unknown as BootstrapWindow),
): CaptureOutcome {
  if (!win) return "noop";
  if (win.location.pathname !== WELCOME_PATH) return "noop";
  if (!hasInviteResidue(win)) return "noop";

  const token = parseAgentInviteFragment(win.location.pathname, win.location.hash);
  if (token) capturedToken = token;

  win.history.replaceState(null, "", WELCOME_PATH);

  return token ? "captured" : "scrubbed";
}

/** What /welcome reads. Null once the token has been used, or after a refresh. */
export function readCapturedAgentInvite(): string | null {
  return capturedToken;
}

/** Drops the credential from memory once redemption has succeeded. */
export function clearCapturedAgentInvite(): void {
  capturedToken = null;
}

/** Test seam. Not used by application code. */
export function __setCapturedAgentInviteForTests(token: string | null): void {
  capturedToken = token;
}

// The self-execution described in the header. Must stay the last statement, and
// this module must stay the first import in src/main.tsx.
captureAgentInviteFromFragment();
